// src/components/Landing/LandingPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Zap, Shield, Globe, Activity, Brain, Server,
  Database, Leaf, Cloud, BarChart2, Bell, Lock, Cpu, Network,
  CheckCircle, ChevronRight, Eye, GitBranch, Layers, Bot,
} from 'lucide-react';

// ─── ProbeStack Logo (same as Shell) ─────────────────────────────────────────
function ProbeStackIcon({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none">
      <defs>
        <linearGradient id="lp-full" x1="44" y1="0" x2="0" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#f97316" />
          <stop offset="48%"  stopColor="#eab308" />
          <stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
        <linearGradient id="lp-orange" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#fb923c" /><stop offset="1" stopColor="#f97316" />
        </linearGradient>
        <linearGradient id="lp-teal" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#2dd4bf" /><stop offset="1" stopColor="#0d9488" />
        </linearGradient>
      </defs>
      <path d="M22 2L37.5 11.5V32.5L22 42L6.5 32.5V11.5Z" fill="url(#lp-full)" fillOpacity="0.13" stroke="url(#lp-full)" strokeWidth="1.6"/>
      <line x1="13" y1="14" x2="13" y2="30" stroke="url(#lp-orange)" strokeWidth="2.6" strokeLinecap="round"/>
      <path d="M13 14 H18.5 C20.98 14 23 16.02 23 18.5 C23 20.98 20.98 23 18.5 23 H13" stroke="url(#lp-orange)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M30.5 17 C30.5 17 29 14.5 26.5 14.5 C24 14.5 22.5 16 23.5 18 C24.5 20 27.5 20.5 28.5 22.5 C29.5 24.5 28 26.5 25.5 26.5 C23 26.5 21.5 25 21.5 25" stroke="url(#lp-teal)" strokeWidth="2.6" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

// ─── Animated counter ─────────────────────────────────────────────────────────
function Counter({ target, suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const start = performance.now();
      const tick  = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const ease = 1 - Math.pow(1 - t, 3);
        setVal(Math.round(ease * target));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

// ─── Architecture SVG diagram ─────────────────────────────────────────────────
function ArchDiagram() {
  const sources = [
    { x: 60,  label: 'GCP',       color: '#4285f4', icon: '☁️' },
    { x: 145, label: 'Azure',     color: '#0078d4', icon: '🔷' },
    { x: 230, label: 'AWS',       color: '#f97316', icon: '🟠' },
    { x: 315, label: 'MongoDB',   color: '#22c55e', icon: '🍃' },
    { x: 400, label: 'Postgres',  color: '#a78bfa', icon: '🗄️' },
    { x: 485, label: 'Elastic',   color: '#f0c000', icon: '🔍' },
    { x: 570, label: 'Kafka',     color: '#b45309', icon: '📨' },
    { x: 655, label: 'Datadog',   color: '#632ca6', icon: '🐕' },
  ];
  const outputs = [
    { x: 80,  label: 'Live Logs',  color: '#60a5fa', icon: '📋' },
    { x: 200, label: 'Alerts',     color: '#f87171', icon: '🔔' },
    { x: 320, label: 'Topology',   color: '#34d399', icon: '🌐' },
    { x: 440, label: 'Security',   color: '#f59e0b', icon: '🛡️' },
    { x: 560, label: 'AI Agents',  color: '#c084fc', icon: '🤖' },
    { x: 680, label: 'AgentCore',  color: '#2dd4bf', icon: '⚡' },
  ];
  const cx = 375; // center X of ProbeStack box

  return (
    <svg viewBox="0 0 760 320" style={{ width: '100%', height: 'auto', maxWidth: 760 }}>
      <defs>
        <linearGradient id="arch-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#f97316" />
          <stop offset="50%"  stopColor="#eab308" />
          <stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <style>{`
          @keyframes dash-in  { from { stroke-dashoffset: 60; } to { stroke-dashoffset: 0; } }
          @keyframes dash-out { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -60; } }
          .line-in  { stroke-dasharray: 6 4; animation: dash-in  2s linear infinite; }
          .line-out { stroke-dasharray: 6 4; animation: dash-out 2s linear infinite; }
          @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
          .src-node { animation: float 3s ease-in-out infinite; }
        `}</style>
      </defs>

      {/* ── Incoming lines (sources → engine) ── */}
      {sources.map((s, i) => (
        <line key={`in-${i}`}
          x1={s.x} y1={62} x2={cx} y2={148}
          stroke={s.color} strokeWidth="1.5" strokeOpacity="0.55"
          className="line-in"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}

      {/* ── Outgoing lines (engine → outputs) ── */}
      {outputs.map((o, i) => (
        <line key={`out-${i}`}
          x1={cx} y1={188} x2={o.x} y2={258}
          stroke={o.color} strokeWidth="1.5" strokeOpacity="0.55"
          className="line-out"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}

      {/* ── Source nodes ── */}
      {sources.map((s, i) => (
        <g key={`src-${i}`} transform={`translate(${s.x - 30}, 12)`}
          className="src-node" style={{ animationDelay: `${i * 0.3}s` }}>
          <rect width="60" height="46" rx="10" fill={s.color} fillOpacity="0.12" stroke={s.color} strokeWidth="1.2" strokeOpacity="0.5"/>
          <text x="30" y="22" textAnchor="middle" fontSize="16">{s.icon}</text>
          <text x="30" y="38" textAnchor="middle" fontSize="8.5" fill={s.color} fontFamily="Inter,sans-serif" fontWeight="600">{s.label}</text>
        </g>
      ))}

      {/* ── ProbeStack Engine box ── */}
      <rect x={cx - 130} y={140} width="260" height="56" rx="14"
        fill="url(#arch-grad)" fillOpacity="0.12"
        stroke="url(#arch-grad)" strokeWidth="2"
        filter="url(#glow)"
      />
      <text x={cx} y={162} textAnchor="middle" fontSize="10" fill="#f97316" fontFamily="Inter,sans-serif" fontWeight="600" letterSpacing="2">KRE ENGINE</text>
      <text x={cx} y={178} textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily="Inter,sans-serif">Ingest · Correlate · Observe · Alert</text>

      {/* ── Output nodes ── */}
      {outputs.map((o, i) => (
        <g key={`out-${i}`} transform={`translate(${o.x - 32}, 260)`}>
          <rect width="64" height="48" rx="10" fill={o.color} fillOpacity="0.12" stroke={o.color} strokeWidth="1.2" strokeOpacity="0.5"/>
          <text x="32" y="20" textAnchor="middle" fontSize="16">{o.icon}</text>
          <text x="32" y="37" textAnchor="middle" fontSize="8" fill={o.color} fontFamily="Inter,sans-serif" fontWeight="600">{o.label.split(' ').map((w,wi) => (
            // Multi-line label hack — just show first line, keep it clean
            wi === 0 ? w : ''
          )).join('')}</text>
          <text x="32" y="47" textAnchor="middle" fontSize="7.5" fill={o.color + 'aa'} fontFamily="Inter,sans-serif">
            {o.label.split(' ')[1] || ''}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, title, desc, color, items }) {
  return (
    <div className="rounded-2xl p-5 flex flex-col gap-3 group hover:scale-[1.02] transition-transform"
      style={{ background: 'var(--bg-secondary)', border: `1px solid ${color}20`, borderTop: `3px solid ${color}` }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <div className="font-bold text-sm mb-1">{title}</div>
        <div className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>{desc}</div>
      </div>
      {items && (
        <ul className="flex flex-col gap-1">
          {items.map(item => (
            <li key={item} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
              <CheckCircle size={10} style={{ color, flexShrink: 0 }} /> {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Integration badge ────────────────────────────────────────────────────────
function IntBadge({ icon, label, color }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl"
      style={{ background: `${color}0a`, border: `1px solid ${color}22` }}>
      <span className="text-lg">{icon}</span>
      <span className="text-xs font-semibold" style={{ color }}>{label}</span>
    </div>
  );
}

// ─── Main landing page ────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 2000);
    return () => clearInterval(id);
  }, []);

  const liveMetrics = [
    { label: 'Log Events / sec',   value: (1200 + (tick % 3) * 47).toLocaleString(), color: '#60a5fa' },
    { label: 'Active Alerts',       value: 3 + (tick % 2),                             color: '#f87171' },
    { label: 'Sources Connected',   value: 12,                                          color: '#34d399' },
    { label: 'AI Agents Monitored', value: 28,                                          color: '#c084fc' },
  ];

  return (
    <div style={{ background: 'var(--bg-primary)', color: 'var(--text)', height: '100vh', overflowY: 'auto', overflowX: 'hidden' }}>

      {/* ── Top nav bar ─────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-8 h-16"
        style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center gap-3">
          <ProbeStackIcon size={32} />
          <span className="font-bold text-base" style={{
            background: 'linear-gradient(90deg,#f97316,#eab308,#14b8a6)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>KRE Observability</span>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm" style={{ color: 'var(--text-3)' }}>
          {['Features', 'Architecture', 'Integrations', 'Security', 'AI'].map(s => (
            <a key={s} href={`#${s.toLowerCase()}`}
              className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>{s}</a>
          ))}
        </div>
        <button onClick={() => navigate('/overview')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg,#f97316,#14b8a6)', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(249,115,22,.4)' }}>
          Enter Platform <ArrowRight size={14} />
        </button>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="relative flex flex-col items-center text-center px-6 pt-20 pb-16 overflow-hidden">
        {/* Background glow blobs */}
        <div style={{ position: 'absolute', top: -60, left: '20%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle,rgba(249,115,22,.12) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 40, right: '15%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle,rgba(20,184,166,.1) 0%,transparent 70%)', pointerEvents: 'none' }} />

        {/* Badge */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
          style={{ background: 'rgba(249,115,22,.1)', border: '1px solid rgba(249,115,22,.3)', color: '#f97316' }}>
          <Zap size={11} /> Enterprise-grade AI Observability Platform
        </div>

        {/* Headline */}
        <h1 className="font-bold leading-tight mb-5"
          style={{ fontSize: 'clamp(2rem,5vw,3.5rem)', lineHeight: 1.1, maxWidth: 800 }}>
          <span style={{
            background: 'linear-gradient(135deg,#f97316 0%,#eab308 40%,#14b8a6 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>Observe Everything.</span>
          <br />
          <span style={{ color: 'var(--text)' }}>Act on Intelligence.</span>
        </h1>

        <p className="text-base mb-8 max-w-2xl leading-relaxed" style={{ color: 'var(--text-2)' }}>
          KRE Observability unifies logs, metrics, traces, alerts, security, and AI agent monitoring
          across your entire cloud estate — with real-time dashboards and intelligent automation.
        </p>

        {/* CTA buttons */}
        <div className="flex items-center gap-3 flex-wrap justify-center mb-14">
          <button onClick={() => navigate('/overview')}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg,#f97316,#eab308,#14b8a6)', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 8px 28px rgba(249,115,22,.35)' }}>
            Launch Dashboard <ArrowRight size={15} />
          </button>
          <button onClick={() => navigate('/connections')}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
            Connect Sources <ChevronRight size={15} />
          </button>
        </div>

        {/* Live metrics strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full max-w-3xl">
          {liveMetrics.map(m => (
            <div key={m.label} className="rounded-2xl px-4 py-4 text-center"
              style={{ background: 'var(--bg-secondary)', border: `1px solid ${m.color}20` }}>
              <div className="mono font-bold text-2xl mb-1" style={{ color: m.color }}>{m.value}</div>
              <div className="text-xs" style={{ color: 'var(--text-3)' }}>{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Architecture diagram ─────────────────────────────────────────────── */}
      <section id="architecture" className="px-6 py-16"
        style={{ borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold mb-2 tracking-widest uppercase" style={{ color: '#f97316' }}>Architecture</div>
            <h2 className="font-bold text-2xl mb-2">Everything Flows Through One Engine</h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: 'var(--text-3)' }}>
              Ingest from any source, correlate events in real time, surface insights across logs, metrics, traces, and AI agents.
            </p>
          </div>
          <div className="rounded-2xl p-6 overflow-x-auto"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <ArchDiagram />
          </div>
          {/* Flow labels */}
          <div className="flex items-center justify-center gap-8 mt-5 flex-wrap">
            {[
              { label: 'Data Sources',  color: '#60a5fa' },
              { label: '→  Ingest & Normalize', color: 'var(--text-3)' },
              { label: '→  Correlate & Alert', color: 'var(--text-3)' },
              { label: '→  Observe & Act', color: '#34d399' },
            ].map(f => (
              <span key={f.label} className="text-xs font-semibold" style={{ color: f.color }}>{f.label}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────────────────────────────────── */}
      <section className="px-6 py-14">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { target: 99,   suffix: '.9%', label: 'Platform Uptime',    color: '#34d399' },
            { target: 12,   suffix: '+',   label: 'Source Connectors',  color: '#60a5fa' },
            { target: 150,  suffix: 'ms',  label: 'Avg Alert Latency',  color: '#f97316' },
            { target: 1000, suffix: '+',   label: 'Events / Second',    color: '#c084fc' },
          ].map(s => (
            <div key={s.label}>
              <div className="font-black mb-1" style={{ fontSize: 40, color: s.color, lineHeight: 1 }}>
                <Counter target={s.target} suffix={s.suffix} />
              </div>
              <div className="text-xs" style={{ color: 'var(--text-3)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────────────── */}
      <section id="features" className="px-6 py-16" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold mb-2 tracking-widest uppercase" style={{ color: '#14b8a6' }}>Features</div>
            <h2 className="font-bold text-2xl mb-2">Everything You Need to Observe at Scale</h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: 'var(--text-3)' }}>
              From live log streaming to AI agent fleet monitoring — all in one unified platform.
            </p>
          </div>
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))' }}>
            <FeatureCard icon={Activity} color="#60a5fa" title="Live Log Streaming"
              desc="Real-time log tailing with millisecond latency. Filter, search, and correlate events across all sources simultaneously."
              items={['Full-text search with regex','Source-level filtering','Structured JSON parsing','Export to S3/GCS']} />
            <FeatureCard icon={Bell} color="#f87171" title="Intelligent Alerts"
              desc="Threshold, anomaly, and composite alert rules. Escalation policies with Slack, PagerDuty, and webhook integrations."
              items={['Multi-condition rules','Silence & acknowledgement','SLA tracking','Alert fatigue reduction']} />
            <FeatureCard icon={Globe} color="#34d399" title="Network Topology"
              desc="Auto-discovered service maps with dependency graphs. Real-time traffic flows and latency heatmaps."
              items={['Service mesh visualization','Dependency impact analysis','Latency p50/p95/p99','Live traffic flows']} />
            <FeatureCard icon={Shield} color="#f59e0b" title="Security & Compliance"
              desc="Identity & Access Management, Zero Trust policies, PCI DSS/SOC 2 dashboards, and threat detection in real time."
              items={['IAM anomaly detection','Zero Trust enforcement','PCI DSS / SOC 2','Audit log trail']} />
            <FeatureCard icon={Brain} color="#c084fc" title="Agent Observability"
              desc="Monitor LLM token usage, cost, latency, groundedness, and toxicity across 6+ models with trace visualization."
              items={['Token cost in dollars','Safety & groundedness scoring','Trace waterfall view','Feedback loop analytics']} />
            <FeatureCard icon={Cpu} color="#2dd4bf" title="AgentCore Fleet"
              desc="Multi-cloud AI agent fleet monitoring. Track runs, tool calls, memory usage, and execution across AWS Bedrock, Azure AI, Vertex, and OpenAI."
              items={['Fleet health overview','Run waterfall traces','Tool call analytics','Memory usage tracking']} />
            <FeatureCard icon={Lock} color="#818cf8" title="RBAC & Governance"
              desc="Fine-grained role-based access control with organizations, projects, and developer management."
              items={['Custom roles & permissions','Project-level isolation','API key management','SSO / SAML ready']} />
            <FeatureCard icon={BarChart2} color="#fb923c" title="Analytics & Insights"
              desc="Cross-source correlated analytics with configurable dashboards, templates, and automated report generation."
              items={['Custom dashboard builder','Anomaly detection','Trend forecasting','Exportable reports']} />
          </div>
        </div>
      </section>

      {/* ── Integrations ──────────────────────────────────────────────────────── */}
      <section id="integrations" className="px-6 py-16" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold mb-2 tracking-widest uppercase" style={{ color: '#eab308' }}>Integrations</div>
            <h2 className="font-bold text-2xl mb-2">Connect Any Source in Minutes</h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: 'var(--text-3)' }}>
              12 production-ready connectors across cloud, database, streaming, and observability platforms.
            </p>
          </div>

          {/* Cloud */}
          <div className="mb-6">
            <div className="text-xs font-semibold mb-3 tracking-wider uppercase" style={{ color: 'var(--text-3)' }}>☁️ Cloud Platforms</div>
            <div className="flex flex-wrap gap-2">
              <IntBadge icon="☁️" label="Google Cloud Platform" color="#4285f4" />
              <IntBadge icon="🔷" label="Microsoft Azure Monitor" color="#0078d4" />
              <IntBadge icon="🟠" label="AWS CloudWatch" color="#f97316" />
            </div>
          </div>

          {/* Database */}
          <div className="mb-6">
            <div className="text-xs font-semibold mb-3 tracking-wider uppercase" style={{ color: 'var(--text-3)' }}>🗄️ Databases</div>
            <div className="flex flex-wrap gap-2">
              <IntBadge icon="🍃" label="MongoDB Atlas" color="#22c55e" />
              <IntBadge icon="🗄️" label="PostgreSQL" color="#a78bfa" />
              <IntBadge icon="🔴" label="Redis / Valkey" color="#dc2626" />
            </div>
          </div>

          {/* Search & Metrics */}
          <div className="mb-6">
            <div className="text-xs font-semibold mb-3 tracking-wider uppercase" style={{ color: 'var(--text-3)' }}>📊 Search, Metrics & Streaming</div>
            <div className="flex flex-wrap gap-2">
              <IntBadge icon="🔍" label="Elasticsearch" color="#f0c000" />
              <IntBadge icon="🔥" label="Prometheus" color="#e6522c" />
              <IntBadge icon="📋" label="Grafana Loki" color="#f27405" />
              <IntBadge icon="📨" label="Apache Kafka" color="#b45309" />
            </div>
          </div>

          {/* Observability Platforms */}
          <div className="mb-6">
            <div className="text-xs font-semibold mb-3 tracking-wider uppercase" style={{ color: 'var(--text-3)' }}>🏢 Observability Platforms</div>
            <div className="flex flex-wrap gap-2">
              <IntBadge icon="🐕" label="Datadog" color="#632ca6" />
              <IntBadge icon="🌀" label="Splunk" color="#ef4444" />
            </div>
          </div>

          {/* AI Providers */}
          <div>
            <div className="text-xs font-semibold mb-3 tracking-wider uppercase" style={{ color: 'var(--text-3)' }}>🤖 AI Agent Providers</div>
            <div className="flex flex-wrap gap-2">
              <IntBadge icon="🟠" label="AWS Bedrock" color="#f97316" />
              <IntBadge icon="🔵" label="Azure AI Foundry" color="#0078d4" />
              <IntBadge icon="🟣" label="Vertex AI" color="#4285f4" />
              <IntBadge icon="🟢" label="OpenAI Agents" color="#10a37f" />
              <IntBadge icon="🧠" label="Anthropic Claude" color="#d97706" />
              <IntBadge icon="🔷" label="Cohere" color="#39d3c3" />
              <IntBadge icon="🌪️" label="Mistral AI" color="#f97316" />
              <IntBadge icon="🤗" label="HuggingFace" color="#fbbf24" />
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Section ────────────────────────────────────────────────────────── */}
      <section id="ai" className="px-6 py-16" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold mb-2 tracking-widest uppercase" style={{ color: '#c084fc' }}>AI Intelligence</div>
            <h2 className="font-bold text-2xl mb-2">Observe Your AI Agents</h2>
            <p className="text-sm max-w-2xl mx-auto" style={{ color: 'var(--text-3)' }}>
              The first enterprise observability platform built for the age of AI agents.
              Monitor LLM costs, safety, performance, and multi-cloud agent fleets.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Token tracking card */}
            <div className="rounded-2xl p-5" style={{ background: 'var(--bg-secondary)', border: '1px solid rgba(192,132,252,.2)', borderTop: '3px solid #c084fc' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(192,132,252,.15)' }}>
                  <Brain size={15} style={{ color: '#c084fc' }} />
                </div>
                <span className="font-bold text-sm">Token & Cost Tracking</span>
              </div>
              <div className="flex flex-col gap-2 text-xs">
                {[
                  { model: 'GPT-4o',            tokens: '2.4M', cost: '$12.00', color: '#10a37f' },
                  { model: 'Claude 3.5 Sonnet', tokens: '1.8M', cost: '$8.10',  color: '#d97706' },
                  { model: 'Gemini 1.5 Pro',    tokens: '3.1M', cost: '$10.85', color: '#4285f4' },
                  { model: 'Llama 3.1 70B',     tokens: '0.9M', cost: '$0.00',  color: '#64748b' },
                ].map(m => (
                  <div key={m.model} className="flex items-center gap-2 py-1.5 rounded-lg px-2.5"
                    style={{ background: `${m.color}08`, border: `1px solid ${m.color}15` }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0, display: 'inline-block' }}/>
                    <span style={{ flex: 1, color: 'var(--text-2)' }}>{m.model}</span>
                    <span className="mono" style={{ color: m.color }}>{m.cost}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Safety scoring card */}
            <div className="rounded-2xl p-5" style={{ background: 'var(--bg-secondary)', border: '1px solid rgba(251,191,36,.2)', borderTop: '3px solid #fbbf24' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(251,191,36,.15)' }}>
                  <Shield size={15} style={{ color: '#fbbf24' }} />
                </div>
                <span className="font-bold text-sm">Safety & Groundedness</span>
              </div>
              <div className="flex flex-col gap-3">
                {[
                  { label: 'Groundedness',    value: 94, color: '#34d399' },
                  { label: 'Toxicity Score',  value: 2,  color: '#f87171', invert: true },
                  { label: 'Relevance',       value: 91, color: '#60a5fa' },
                  { label: 'Hallucination',   value: 6,  color: '#fb923c', invert: true },
                ].map(s => (
                  <div key={s.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: 'var(--text-2)' }}>{s.label}</span>
                      <span style={{ color: s.color, fontWeight: 700 }}>{s.value}%</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
                      <div style={{ width: `${s.value}%`, height: '100%', background: s.color, borderRadius: 99 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Fleet card */}
            <div className="rounded-2xl p-5" style={{ background: 'var(--bg-secondary)', border: '1px solid rgba(45,212,191,.2)', borderTop: '3px solid #2dd4bf' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(45,212,191,.15)' }}>
                  <Cpu size={15} style={{ color: '#2dd4bf' }} />
                </div>
                <span className="font-bold text-sm">Agent Fleet Status</span>
              </div>
              <div className="flex flex-col gap-2 text-xs">
                {[
                  { provider: 'AWS Bedrock',   agents: 3, active: 2, color: '#f97316' },
                  { provider: 'Azure AI',       agents: 3, active: 3, color: '#0078d4' },
                  { provider: 'Vertex AI',      agents: 3, active: 1, color: '#4285f4' },
                  { provider: 'OpenAI',         agents: 3, active: 3, color: '#10a37f' },
                ].map(p => (
                  <div key={p.provider} className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg"
                    style={{ background: `${p.color}08`, border: `1px solid ${p.color}15` }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color, flexShrink: 0, display: 'inline-block' }}/>
                    <span style={{ flex: 1, color: 'var(--text-2)' }}>{p.provider}</span>
                    <span className="mono" style={{ color: p.active === p.agents ? '#34d399' : '#f59e0b' }}>
                      {p.active}/{p.agents}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 flex items-center justify-between text-xs" style={{ borderTop: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text-3)' }}>Total agents</span>
                <span className="mono font-bold" style={{ color: '#2dd4bf' }}>12 agents · 9 active</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Security Section ──────────────────────────────────────────────────── */}
      <section id="security" className="px-6 py-16"
        style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold mb-2 tracking-widest uppercase" style={{ color: '#f59e0b' }}>Security</div>
            <h2 className="font-bold text-2xl mb-2">Enterprise Security Built In</h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: 'var(--text-3)' }}>
              SOC 2, PCI DSS, Zero Trust, and full audit trails — security is not an add-on.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: '🔐', title: 'Identity & Access',
                color: '#60a5fa', badge: 'IAM',
                items: ['MFA enforcement', 'SSO / SAML / OIDC', 'Just-in-time provisioning', 'Privileged access reviews'],
              },
              {
                icon: '📋', title: 'Compliance Frameworks',
                color: '#f59e0b', badge: 'SOC 2 · PCI DSS',
                items: ['Automated evidence collection', 'Continuous compliance scoring', 'Policy-as-code', 'Audit-ready reports'],
              },
              {
                icon: '🛡️', title: 'Zero Trust Architecture',
                color: '#34d399', badge: 'Zero Trust',
                items: ['Micro-segmentation', 'Workload identity verification', 'Encrypted service mesh', 'Behavioral anomaly detection'],
              },
              {
                icon: '🔒', title: 'Data Protection',
                color: '#c084fc', badge: 'Encryption',
                items: ['AES-256 at rest', 'TLS 1.3 in transit', 'Key rotation policies', 'Data residency controls'],
              },
              {
                icon: '⚠️', title: 'Threat Detection',
                color: '#f87171', badge: 'SIEM',
                items: ['ML anomaly detection', 'Lateral movement alerts', 'MITRE ATT&CK mapping', 'Automated response playbooks'],
              },
              {
                icon: '📊', title: 'Monitoring & Incident',
                color: '#fb923c', badge: 'MTTD · MTTR',
                items: ['Real-time threat scoring', 'Incident timelines', 'War-room collaboration', 'Post-incident analytics'],
              },
            ].map(c => (
              <div key={c.title} className="rounded-2xl p-4"
                style={{ background: 'var(--bg-primary)', border: `1px solid ${c.color}20` }}>
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">{c.icon}</span>
                  <div>
                    <div className="font-bold text-sm">{c.title}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${c.color}15`, color: c.color, border: `1px solid ${c.color}25` }}>{c.badge}</span>
                  </div>
                </div>
                <ul className="flex flex-col gap-1">
                  {c.items.map(item => (
                    <li key={item} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-2)' }}>
                      <CheckCircle size={9} style={{ color: c.color, flexShrink: 0 }} /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platform Overview ─────────────────────────────────────────────────── */}
      <section className="px-6 py-16" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold mb-2 tracking-widest uppercase" style={{ color: '#60a5fa' }}>Platform</div>
            <h2 className="font-bold text-2xl mb-2">The Complete Observability Stack</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Left: feature list */}
            <div className="flex flex-col gap-3">
              {[
                { icon: Activity,    label: 'Live Log Streaming',       desc: 'Tail logs in real time from any source',          to: '/logs',            color: '#60a5fa' },
                { icon: Bell,        label: 'Smart Alert Engine',        desc: 'ML-powered anomaly detection & escalation',       to: '/alerts',          color: '#f87171' },
                { icon: Globe,       label: 'Service Topology',          desc: 'Auto-discovered dependency maps',                 to: '/topology',        color: '#34d399' },
                { icon: Shield,      label: 'Security Dashboard',        desc: 'Unified security posture & compliance',           to: '/security',        color: '#f59e0b' },
                { icon: Brain,       label: 'Agent Observability',       desc: 'LLM cost, safety & trace monitoring',             to: '/agent-obs',       color: '#c084fc' },
                { icon: Cpu,         label: 'AgentCore Fleet',           desc: 'Multi-cloud AI agent fleet management',           to: '/agent-core',      color: '#2dd4bf' },
                { icon: Layers,      label: 'Source Connectors',         desc: '12 pre-built data source connectors',             to: '/connections',     color: '#fb923c' },
                { icon: Lock,        label: 'RBAC & Governance',         desc: 'Fine-grained access control',                     to: '/rbac',            color: '#818cf8' },
              ].map(item => (
                <button key={item.label} onClick={() => navigate(item.to)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-left group hover:scale-[1.01] transition-transform"
                  style={{ background: 'var(--bg-secondary)', border: `1px solid ${item.color}18`, cursor: 'pointer' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${item.color}15` }}>
                    <item.icon size={14} style={{ color: item.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">{item.label}</div>
                    <div className="text-xs" style={{ color: 'var(--text-3)' }}>{item.desc}</div>
                  </div>
                  <ChevronRight size={13} style={{ color: 'var(--text-3)' }} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              ))}
            </div>

            {/* Right: mini dashboard mockup */}
            <div className="rounded-2xl p-5 flex flex-col gap-3"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
              {/* Fake topbar */}
              <div className="flex items-center gap-2 pb-2" style={{ borderBottom: '1px solid var(--border)' }}>
                <ProbeStackIcon size={20} />
                <span className="text-xs font-bold" style={{ background: 'linear-gradient(90deg,#f97316,#14b8a6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>KRE Observability</span>
                <div className="ml-auto flex gap-1">
                  {['GCP','Azure','DB'].map(s => (
                    <span key={s} className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,.25)', fontSize: 9 }}>● {s}</span>
                  ))}
                </div>
              </div>
              {/* KPI row */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Events', value: '24.3K', color: '#60a5fa' },
                  { label: 'Errors', value: '127',   color: '#f87171' },
                  { label: 'Alerts', value: '3',     color: '#f59e0b' },
                  { label: 'P95',    value: '142ms',  color: '#34d399' },
                ].map(k => (
                  <div key={k.label} className="rounded-lg p-2 text-center" style={{ background: 'var(--bg-primary)' }}>
                    <div className="mono font-bold text-sm" style={{ color: k.color }}>{k.value}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-3)' }}>{k.label}</div>
                  </div>
                ))}
              </div>
              {/* Fake bar chart */}
              <div className="flex items-end gap-1 h-24 pt-2">
                {[45,62,38,55,78,49,65,82,44,70,59,88,52,76,61,44,90,68,55,72].map((h, i) => (
                  <div key={i} style={{ flex: 1, height: `${h}%`, background: `linear-gradient(180deg,#60a5fa,#3b82f6)`, borderRadius: '2px 2px 0 0', opacity: 0.7 + (i % 3) * 0.1 }} />
                ))}
              </div>
              <div className="text-xs text-center" style={{ color: 'var(--text-3)' }}>Event volume — last 20 minutes</div>
              {/* Source pills */}
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: 'GCP', color: '#4285f4' }, { label: 'Azure', color: '#0078d4' },
                  { label: 'MongoDB', color: '#22c55e' }, { label: 'PostgreSQL', color: '#a78bfa' },
                  { label: 'Kafka', color: '#b45309' }, { label: 'Prometheus', color: '#e6522c' },
                ].map(s => (
                  <span key={s.label} className="text-xs px-2 py-0.5 rounded-full"
                    style={{ background: `${s.color}12`, color: s.color, border: `1px solid ${s.color}25`, fontSize: 10 }}>
                    {s.label}
                  </span>
                ))}
              </div>
              {/* Fake log lines */}
              <div className="flex flex-col gap-1 rounded-lg p-2.5" style={{ background: 'var(--bg-primary)', fontFamily: 'Space Mono, monospace' }}>
                {[
                  { lvl: 'INFO',  msg: '[gcp] 2847 events ingested',        color: '#60a5fa' },
                  { lvl: 'WARN',  msg: '[azure] latency spike: 340ms',      color: '#fbbf24' },
                  { lvl: 'ERROR', msg: '[mongodb] replica lag 3.2s',        color: '#f87171' },
                  { lvl: 'INFO',  msg: '[kafka] consumer lag: 0',           color: '#60a5fa' },
                ].map((l, i) => (
                  <div key={i} className="flex items-center gap-2" style={{ fontSize: 9 }}>
                    <span style={{ color: l.color, fontWeight: 700, width: 36, flexShrink: 0 }}>{l.lvl}</span>
                    <span style={{ color: 'var(--text-3)' }}>{l.msg}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────────── */}
      <section className="px-6 py-20 text-center" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="max-w-2xl mx-auto">
          {/* Big glow */}
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 28 }}>
            <div style={{ position: 'absolute', inset: -20, borderRadius: '50%', background: 'radial-gradient(circle,rgba(249,115,22,.2) 0%,transparent 70%)', pointerEvents: 'none' }} />
            <ProbeStackIcon size={72} />
          </div>
          <h2 className="font-black mb-4" style={{ fontSize: 'clamp(1.5rem,4vw,2.5rem)', lineHeight: 1.15 }}>
            <span style={{ background: 'linear-gradient(135deg,#f97316,#eab308,#14b8a6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Ready to Observe Everything?
            </span>
          </h2>
          <p className="mb-8 text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
            Connect your first data source in under 2 minutes. No agent to install. Full demo data available immediately.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => navigate('/overview')}
              className="flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-sm transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg,#f97316,#eab308,#14b8a6)', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 10px 36px rgba(249,115,22,.4)' }}>
              Launch Platform <ArrowRight size={16} />
            </button>
            <button onClick={() => navigate('/connections')}
              className="flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all hover:scale-105"
              style={{ background: 'transparent', border: '1px solid var(--border-2)', color: 'var(--text-2)', cursor: 'pointer' }}>
              <Layers size={14} /> Connect Sources
            </button>
          </div>
          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-10 flex-wrap">
            {['SOC 2 Type II', 'PCI DSS', 'Zero Trust', 'GDPR Ready', 'HIPAA Compliant'].map(b => (
              <div key={b} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-3)' }}>
                <CheckCircle size={11} style={{ color: '#34d399' }} /> {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="px-8 py-6 flex items-center justify-between flex-wrap gap-3"
        style={{ borderTop: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2">
          <ProbeStackIcon size={20} />
          <span className="text-xs font-bold" style={{ background: 'linear-gradient(90deg,#f97316,#14b8a6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>KRE Observability</span>
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>© 2026 · Enterprise Observability Platform</span>
        </div>
        <div className="flex items-center gap-4">
          {[
            { label: 'Dashboard', to: '/overview' },
            { label: 'Sources',   to: '/connections' },
            { label: 'Security',  to: '/security' },
            { label: 'AI Agents', to: '/agent-obs' },
          ].map(l => (
            <button key={l.label} onClick={() => navigate(l.to)}
              className="text-xs transition-colors"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)' }}>
              {l.label}
            </button>
          ))}
        </div>
      </footer>
    </div>
  );
}
